import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { isAxiosError } from 'axios';
import { Box, styled } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import { ApprovalOutlineInput } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  buildProbationTargetContentHtml,
  ProbationTargetSnapshot,
} from '@/components/Approval/@components/ProbationEvaluation';
import { getProbationDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildProbationSubject,
  PROBATION_TARGET_NOT_FOUND_MESSAGE,
  toProbationTarget,
  type ProbationDraftSource,
} from '@/utils/approval/probationEvaluation';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const InputRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const IdInput = styled(ApprovalOutlineInput)`
  width: 320px;
  max-width: 100%;
`;

/** 수습평가서(188) 대상자 — 레거시 appr_edit_forms_probationary_assessment */
const ProbationEvaluationField = () => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'probationEvaluation' });
  const [userId, setUserId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const clearTarget = () =>
    setValue('probationEvaluation', null, { shouldDirty: true });

  const handleConfirm = async () => {
    const id = userId.trim().toLowerCase();
    setUserId(id);
    setIsLoading(true);
    let source: ProbationDraftSource | null = null;
    try {
      source = id
        ? await getNestedDataFetcher(getProbationDraftSourceUrl(id))
        : null;
    } catch (e) {
      // 422 는 레거시에서 빈 결과와 같은 경로(형식 오류 ID)
      if (!isAxiosError(e) || e.response?.status !== 422) {
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
        setIsLoading(false);
        return;
      }
    }
    setIsLoading(false);

    const target = toProbationTarget(source);
    if (!source || !target) {
      // 레거시 — 실패 시 표를 숨기고 직접 고친 제목도 기본 문구로 되돌림
      clearTarget();
      setValue('subject', buildProbationSubject(t));
      snackbar.showError({ message: t(PROBATION_TARGET_NOT_FOUND_MESSAGE) });
      return;
    }
    setValue(
      'probationEvaluation',
      {
        targetEmployeeNumber: source.attributes.targetEmployeeNumber,
        content: buildProbationTargetContentHtml(target, t),
      },
      { shouldDirty: true },
    );
    setValue('subject', buildProbationSubject(t, target.name));
  };

  // 레거시 Reset 은 제목을 건드리지 않음
  const handleReset = () => {
    setUserId('');
    clearTarget();
  };

  return (
    <>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('수습평가 대상자 ID')}</InfoSectionTitle>
        </FieldHeader>
        <InputRow>
          <IdInput
            id="probation-target-id"
            value={userId}
            readOnly={!!state}
            onChange={(e) => setUserId(e.target.value)}
          />
          <BoxButton
            variant="secondary"
            size="small"
            // 임시저장 복원 상태는 ID 가 비어 있어 누르면 복원된 대상자가 지워짐
            disabled={isLoading || (!!state && !userId)}
            onClick={handleConfirm}
          >
            {t('확인')}
          </BoxButton>
          <BoxButton
            variant="secondary"
            size="small"
            disabled={isLoading}
            onClick={handleReset}
          >
            Reset
          </BoxButton>
        </InputRow>
      </FieldBlock>
      {state && (
        <FieldBlock>
          <ProbationTargetSnapshot content={state.content} />
        </FieldBlock>
      )}
    </>
  );
};

export default ProbationEvaluationField;
